import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import {
  MOCK_DOCUMENTS,
  INITIAL_COMMENTS,
  type ReviewDocument,
  type Comment,
} from '../data/mockDocuments'
import {
  DISPLAY_TYPES,
  isBusinessEmail,
  getThreadParents,
  getThreadChildren,
  useClickOutside,
  DEFAULT_REVIEW_STATE,
  type ColumnId,
  type SortColumn,
  type SortDirection,
  type Classification,
  type DocReviewState,
  type CaseIssue,
  type FamilyMember,
  type DisplayRow,
  type FilterSnapshot,
  type SavedCohort,
  type AiMessage,
  type AiConversation,
} from '../components/review/types'
import {
  INITIAL_DOC_STATES,
  DEFAULT_CASE_ISSUES,
  INITIAL_DOC_ISSUES,
} from '../data/mockReviewState'

// ── Helpers that need MOCK_DOCUMENTS ──────────────────────────────────
const ALL_SENDERS = Array.from(
  new Set(MOCK_DOCUMENTS.map((d) => d.sender)),
).sort()

function getThreadSize(threadId: string): number {
  return MOCK_DOCUMENTS.filter((d) => d.threadId === threadId).length
}

function getDocFamily(doc: ReviewDocument): FamilyMember[] {
  if (!doc.threadId) {
    if (doc.type === 'Contract' || doc.type === 'Report') {
      return [
        { id: doc.id, name: doc.subject, type: 'parent' },
        {
          id: `${doc.id}-att1`,
          name: `${doc.subject.split('—')[0].trim()}.pdf`,
          type: 'attachment',
          fileType: 'PDF',
          size: '2.4 MB',
        },
        {
          id: `${doc.id}-att2`,
          name: 'Signature_Page.pdf',
          type: 'attachment',
          fileType: 'PDF',
          size: '145 KB',
        },
      ]
    }
    if (doc.type === 'Invoice') {
      return [
        { id: doc.id, name: doc.subject, type: 'parent' },
        {
          id: `${doc.id}-att1`,
          name: 'Invoice_Detail.xlsx',
          type: 'attachment',
          fileType: 'XLSX',
          size: '89 KB',
        },
      ]
    }
    return [{ id: doc.id, name: doc.subject, type: 'parent' }]
  }
  const threadDocs = MOCK_DOCUMENTS.filter(
    (d) => d.threadId === doc.threadId,
  ).sort((a, b) => (a.threadPosition ?? 0) - (b.threadPosition ?? 0))
  const members: FamilyMember[] = threadDocs.map((d) => ({
    id: d.id,
    name: d.subject,
    type: d.threadPosition === 1 ? 'parent' : 'attachment',
  }))
  const terminal = threadDocs.find((d) => d.isTerminal)
  if (terminal) {
    members.push({
      id: `${terminal.id}-att1`,
      name: 'Summary_Compilation.pdf',
      type: 'attachment',
      fileType: 'PDF',
      size: '1.8 MB',
    })
  }
  return members
}

// ══════════════════════════════════════════════════════════════════════
// HOOK
// ══════════════════════════════════════════════════════════════════════
export function useReviewState() {
  // ── Filter state ──────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [typeFilters, setTypeFilters] = useState<Set<string>>(
    new Set(DISPLAY_TYPES),
  )
  const [threadFilter, setThreadFilter] = useState<
    'all' | 'terminal' | 'standalone'
  >('all')
  const [reviewFilter, setReviewFilter] = useState<
    'all' | 'reviewed' | 'unreviewed'
  >('all')
  const [classificationFilter, setClassificationFilter] = useState<
    'all' | 'hot' | 'critical' | 'tbd' | 'unclassified'
  >('all')
  const [privilegedFilter, setPrivilegedFilter] = useState<
    'all' | 'privileged' | 'not-privileged'
  >('all')
  const [issueFilter, setIssueFilter] = useState<string | null>(null)
  const [commentFilter, setCommentFilter] = useState<'all' | 'with-comments'>(
    'all',
  )
  const [groupByFamily, setGroupByFamily] = useState(false)
  const [hideDuplicates, setHideDuplicates] = useState(false)
  const [participantSearch, setParticipantSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(),
  )

  // ── Sort state ────────────────────────────────────────────────────
  const [sortColumn, setSortColumn] = useState<SortColumn>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // ── Pagination state ──────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<25 | 50 | 100>(25)

  // ── Thread state ──────────────────────────────────────────────────
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set())

  // ── Document review state ─────────────────────────────────────────
  const [docStates, setDocStates] =
    useState<Record<string, DocReviewState>>(INITIAL_DOC_STATES)
  const getDocState = (docId: string): DocReviewState =>
    docStates[docId] || DEFAULT_REVIEW_STATE

  // ── Case issues state ─────────────────────────────────────────────
  const [caseIssues] = useState<CaseIssue[]>(DEFAULT_CASE_ISSUES)
  const [docIssues, setDocIssues] =
    useState<Record<string, string[]>>(INITIAL_DOC_ISSUES)

  // ── Drawer state ──────────────────────────────────────────────────
  const [selectedDoc, setSelectedDocRaw] = useState<ReviewDocument | null>(null)
  const setSelectedDoc = useCallback((doc: ReviewDocument | null) => {
    setSelectedDocRaw(doc)
    if (doc) setAiDrawerOpen(false)
  }, [])
  const [drawerTab, setDrawerTab] = useState<'summary' | 'family' | 'comments'>(
    'summary',
  )
  const [comments, setComments] =
    useState<Record<string, Comment[]>>(INITIAL_COMMENTS)
  const [newComment, setNewComment] = useState('')
  const commentsEndRef = useRef<HTMLDivElement>(null)
  const commentInputRef = useRef<HTMLTextAreaElement>(null)

  // ── Preview / hover state ─────────────────────────────────────────
  const [previewDoc, setPreviewDoc] = useState<ReviewDocument | null>(null)
  const [hoveredDoc, setHoveredDoc] = useState<ReviewDocument | null>(null)
  const [hoverPosition, setHoverPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Column visibility state ───────────────────────────────────────
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnId>>(
    new Set<ColumnId>([
      'date',
      'type',
      'subject',
      'summary',
      'thread',
      'action',
      'id',
    ]),
  )
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false)
  const columnDropdownRef = useRef<HTMLDivElement>(null)

  const gridTemplate = useMemo(() => {
    const allCols: { id: ColumnId; width: string }[] = [
      { id: 'date', width: '90px' },
      { id: 'type', width: '90px' },
      { id: 'subject', width: '1fr' },
      { id: 'summary', width: '1fr' },
      { id: 'sender', width: '140px' },
      { id: 'status', width: '110px' },
      { id: 'thread', width: '44px' },
      { id: 'action', width: '50px' },
      { id: 'id', width: '100px' },
    ]
    const widths = allCols
      .filter((col) => visibleColumns.has(col.id))
      .map((col) => col.width)
    widths.push('32px') // gear icon column
    return widths.join(' ')
  }, [visibleColumns])

  const isColumnVisible = (id: ColumnId) => visibleColumns.has(id)

  const toggleColumn = (id: ColumnId) => {
    if (id === 'subject') return
    setVisibleColumns((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // ── Dropdown state ────────────────────────────────────────────────
  const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false)
  const pageSizeDropdownRef = useRef<HTMLDivElement>(null)
  useClickOutside(pageSizeDropdownRef, () => setPageSizeDropdownOpen(false))
  useClickOutside(columnDropdownRef, () => setColumnDropdownOpen(false))

  // ── AI state ──────────────────────────────────────────────────────
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false)
  const [aiMessages, setAiMessages] = useState<AiMessage[]>([])
  const [aiHistory, setAiHistory] = useState<AiConversation[]>([])
  const [aiInput, setAiInput] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiFilteredDocIds, setAiFilteredDocIds] = useState<string[] | null>(
    null,
  )
  const [aiTab, setAiTab] = useState<'chat' | 'history'>('chat')
  const aiMessagesEndRef = useRef<HTMLDivElement>(null)

  const openAiDrawer = useCallback(() => {
    setAiDrawerOpen(true)
    setSelectedDoc(null)
    setAiTab('chat')
  }, [])

  const closeAiDrawer = useCallback(() => {
    setAiDrawerOpen(false)
  }, [])

  // Mock AI response logic
  const handleAiSend = useCallback(() => {
    const text = aiInput.trim()
    if (!text || aiLoading) return

    const userMsg: AiMessage = {
      id: `ai-msg-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
    }
    setAiMessages((prev) => [...prev, userMsg])
    setAiInput('')
    setAiLoading(true)

    // Determine if this is a cohort query or an answer query
    const cohortKeywords = [
      'show',
      'list',
      'find',
      'give me',
      'which',
      'where',
      'all docs',
      'all documents',
      'mention',
      'contain',
      'related',
      'about',
    ]
    const isCohortQuery = cohortKeywords.some((kw) =>
      text.toLowerCase().includes(kw),
    )

    setTimeout(() => {
      if (isCohortQuery) {
        // Pick random docs from the current filtered set as mock cohort
        const pool = MOCK_DOCUMENTS
        const count = Math.min(pool.length, Math.floor(Math.random() * 11) + 5)
        const shuffled = [...pool].sort(() => Math.random() - 0.5)
        const cohortDocs = shuffled.slice(0, count)
        const cohortIds = cohortDocs.map((d) => d.id)

        const aiMsg: AiMessage = {
          id: `ai-msg-${Date.now() + 1}`,
          role: 'ai',
          content: `Found ${count} documents matching your query. These documents reference topics related to "${text}".`,
          timestamp: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          resultType: 'cohort',
          cohortDocIds: cohortIds,
        }
        setAiMessages((prev) => [...prev, aiMsg])
      } else {
        // Text answer
        const answers = [
          `Based on the documents in this review set, the earliest dated document is from January 2023, a contract between Acme Corp and Smith Associates regarding the initial supply agreement.`,
          `The review set contains 2,847 documents spanning from January 2023 to October 2023. The majority (62%) are email communications, with contracts and memos making up the remainder.`,
          `There are 89 documents currently flagged as privileged, primarily attorney-client communications between Sarah Chen and the external counsel team at Smith & Associates.`,
          `The most frequently referenced entity across documents is "Amendment 3 to the Supply Agreement," appearing in 156 documents across email threads and contract revisions.`,
        ]
        const aiMsg: AiMessage = {
          id: `ai-msg-${Date.now() + 1}`,
          role: 'ai',
          content: answers[Math.floor(Math.random() * answers.length)],
          timestamp: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          resultType: 'answer',
        }
        setAiMessages((prev) => [...prev, aiMsg])
      }
      setAiLoading(false)
    }, 1200)
  }, [aiInput, aiLoading])

  const applyAiCohort = useCallback((docIds: string[]) => {
    setAiFilteredDocIds(docIds)
    setAiDrawerOpen(false)
    setCurrentPage(1)
  }, [])

  const clearAiFilter = useCallback(() => {
    setAiFilteredDocIds(null)
    setCurrentPage(1)
  }, [])

  // Save current conversation to history when starting a new one or closing
  const saveAiConversationToHistory = useCallback(() => {
    if (aiMessages.length === 0) return
    const firstUserMsg = aiMessages.find((m) => m.role === 'user')
    const lastAiMsg = [...aiMessages].reverse().find((m) => m.role === 'ai')
    const conv: AiConversation = {
      id: `ai-conv-${Date.now()}`,
      title: firstUserMsg?.content.slice(0, 60) || 'Untitled',
      messages: [...aiMessages],
      createdAt: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      cohortDocIds: lastAiMsg?.cohortDocIds,
    }
    setAiHistory((prev) => [conv, ...prev])
    setAiMessages([])
  }, [aiMessages])

  const startNewAiChat = useCallback(() => {
    saveAiConversationToHistory()
    setAiTab('chat')
  }, [saveAiConversationToHistory])

  const loadAiConversation = useCallback(
    (conv: AiConversation) => {
      saveAiConversationToHistory()
      setAiMessages(conv.messages)
      setAiTab('chat')
    },
    [saveAiConversationToHistory],
  )

  // ── Filter pipeline ───────────────────────────────────────────────
  const filteredDocs = useMemo(() => {
    let docs = [...MOCK_DOCUMENTS]
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      docs = docs.filter(
        (d) =>
          d.id.toLowerCase().includes(q) ||
          d.date.includes(q) ||
          d.type.toLowerCase().includes(q) ||
          d.sender.toLowerCase().includes(q) ||
          d.subject.toLowerCase().includes(q) ||
          d.summary.toLowerCase().includes(q),
      )
    }
    if (typeFilters.size < DISPLAY_TYPES.length) {
      docs = docs.filter((d) => {
        if (d.type === 'Email') {
          const isBusiness = isBusinessEmail(d.sender)
          if (isBusiness && typeFilters.has('Email - Business')) return true
          if (!isBusiness && typeFilters.has('Email - Personal')) return true
          return false
        }
        if (d.type === 'Contract' && typeFilters.has('Legal Agreement'))
          return true
        if (
          d.type === 'Report' &&
          typeFilters.has('Financial / Technical Report')
        )
          return true
        if (d.type === 'Memo' && typeFilters.has('Internal Memo')) return true
        if (d.type === 'Invoice' && typeFilters.has('Invoice / Receipt'))
          return true
        if (d.type === 'Letter' && typeFilters.has('Correspondence'))
          return true
        return false
      })
    }
    if (threadFilter === 'terminal') {
      docs = docs.filter((d) => d.isTerminal)
    } else if (threadFilter === 'standalone') {
      docs = docs.filter((d) => !d.threadId)
    }
    if (participantSearch.trim()) {
      const q = participantSearch.toLowerCase()
      docs = docs.filter((d) => d.sender.toLowerCase().includes(q))
    }
    if (dateFrom) docs = docs.filter((d) => d.date >= dateFrom)
    if (dateTo) docs = docs.filter((d) => d.date <= dateTo)
    if (reviewFilter !== 'all') {
      docs = docs.filter((d) => {
        const s = docStates[d.id] || DEFAULT_REVIEW_STATE
        return reviewFilter === 'reviewed' ? s.reviewed : !s.reviewed
      })
    }
    if (classificationFilter !== 'all') {
      docs = docs.filter((d) => {
        const s = docStates[d.id] || DEFAULT_REVIEW_STATE
        if (classificationFilter === 'unclassified')
          return s.classification === null
        return s.classification === classificationFilter
      })
    }
    if (privilegedFilter !== 'all') {
      docs = docs.filter((d) => {
        const s = docStates[d.id] || DEFAULT_REVIEW_STATE
        return privilegedFilter === 'privileged' ? s.privileged : !s.privileged
      })
    }
    if (issueFilter) {
      docs = docs.filter((d) => {
        const issues = docIssues[d.id] || []
        return issues.includes(issueFilter)
      })
    }
    if (commentFilter === 'with-comments') {
      docs = docs.filter((d) => (comments[d.id] || []).length > 0)
    }
    if (aiFilteredDocIds) {
      const idSet = new Set(aiFilteredDocIds)
      docs = docs.filter((d) => idSet.has(d.id))
    }
    return docs
  }, [
    searchQuery,
    typeFilters,
    threadFilter,
    participantSearch,
    dateFrom,
    dateTo,
    reviewFilter,
    classificationFilter,
    privilegedFilter,
    commentFilter,
    comments,
    docStates,
    issueFilter,
    docIssues,
    aiFilteredDocIds,
  ])

  // ── Sorting ───────────────────────────────────────────────────────
  const sortedDocs = useMemo(() => {
    if (!sortColumn) return filteredDocs
    const sorted = [...filteredDocs]
    sorted.sort((a, b) => {
      let aVal = a[sortColumn] ?? ''
      let bVal = b[sortColumn] ?? ''
      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }, [filteredDocs, sortColumn, sortDirection])

  // ── Build display rows (with thread grouping) ─────────────────────
  const displayRows = useMemo(() => {
    const threadParents = getThreadParents(MOCK_DOCUMENTS)
    const rows: DisplayRow[] = []
    const seen = new Set<string>()
    for (const doc of sortedDocs) {
      if (seen.has(doc.id)) continue
      seen.add(doc.id)
      if (doc.threadId && doc.threadPosition !== 1) {
        const parent = threadParents.get(doc.threadId)
        if (parent && !seen.has(parent.id)) {
          seen.add(parent.id)
          rows.push({ doc: parent, isChild: false, isTerminal: false })
          if (
            (searchQuery.trim() || participantSearch.trim()) &&
            !expandedThreads.has(doc.threadId)
          ) {
            setExpandedThreads((prev) => new Set(prev).add(doc.threadId))
          }
        }
        if (!expandedThreads.has(doc.threadId)) continue
      }
      if (
        !doc.threadId ||
        doc.threadPosition === 1 ||
        !expandedThreads.has(doc.threadId)
      ) {
        if (doc.threadId && doc.threadPosition !== 1) continue
        rows.push({ doc, isChild: false, isTerminal: false })
      }
      if (
        doc.threadId &&
        doc.threadPosition === 1 &&
        expandedThreads.has(doc.threadId)
      ) {
        const children = getThreadChildren(MOCK_DOCUMENTS, doc.threadId)
        for (const child of children) {
          seen.add(child.id)
          rows.push({
            doc: child,
            isChild: true,
            isTerminal: !!child.isTerminal,
          })
        }
      }
    }
    return rows
  }, [sortedDocs, expandedThreads, searchQuery, participantSearch])

  // ── Pagination ────────────────────────────────────────────────────
  const totalFiltered = filteredDocs.filter(
    (d) => !d.threadId || d.threadPosition === 1,
  ).length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize))
  const safeCurrentPage = Math.min(currentPage, totalPages)

  const paginatedRows = useMemo(() => {
    const startParentIdx = (safeCurrentPage - 1) * pageSize
    const endParentIdx = startParentIdx + pageSize
    let parentCount = 0
    const result: DisplayRow[] = []
    for (const row of displayRows) {
      if (!row.isChild) {
        if (parentCount >= startParentIdx && parentCount < endParentIdx) {
          result.push(row)
        }
        parentCount++
      } else {
        if (
          result.length > 0 &&
          parentCount > startParentIdx &&
          parentCount <= endParentIdx
        ) {
          result.push(row)
        }
      }
    }
    return result
  }, [displayRows, safeCurrentPage, pageSize])

  const showingFrom =
    totalFiltered === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1
  const showingTo = Math.min(safeCurrentPage * pageSize, totalFiltered)

  // ── Reset effects ─────────────────────────────────────────────────
  useEffect(() => {
    setDrawerTab('summary')
    setNewComment('')
  }, [selectedDoc?.id])

  useEffect(() => {
    setCurrentPage(1)
  }, [
    searchQuery,
    typeFilters,
    threadFilter,
    participantSearch,
    dateFrom,
    dateTo,
    sortColumn,
    sortDirection,
    pageSize,
    reviewFilter,
    classificationFilter,
    privilegedFilter,
    issueFilter,
  ])

  // ── Sort toggle handler ───────────────────────────────────────────
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') setSortDirection('desc')
      else setSortColumn(null)
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // ── Thread toggle ─────────────────────────────────────────────────
  const toggleThread = (threadId: string) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev)
      if (next.has(threadId)) next.delete(threadId)
      else next.add(threadId)
      return next
    })
  }

  // ── Filter helpers ────────────────────────────────────────────────
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (typeFilters.size < DISPLAY_TYPES.length) count++
    if (threadFilter !== 'all') count++
    if (dateFrom || dateTo) count++
    if (participantSearch) count++
    if (groupByFamily) count++
    if (hideDuplicates) count++
    if (reviewFilter !== 'all') count++
    if (classificationFilter !== 'all') count++
    if (privilegedFilter !== 'all') count++
    if (issueFilter) count++
    if (commentFilter !== 'all') count++
    return count
  }, [
    typeFilters,
    threadFilter,
    dateFrom,
    dateTo,
    participantSearch,
    groupByFamily,
    hideDuplicates,
    reviewFilter,
    classificationFilter,
    privilegedFilter,
    issueFilter,
    commentFilter,
  ])

  const hasActiveFilters = activeFilterCount > 0

  const clearAllFilters = () => {
    setSearchQuery('')
    setTypeFilters(new Set(DISPLAY_TYPES))
    setThreadFilter('all')
    setDateFrom('')
    setDateTo('')
    setParticipantSearch('')
    setGroupByFamily(false)
    setHideDuplicates(false)
    setReviewFilter('all')
    setClassificationFilter('all')
    setPrivilegedFilter('all')
    setIssueFilter(null)
    setCommentFilter('all')
  }

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  const toggleTypeFilter = (type: string) => {
    setTypeFilters((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  // ── Cohort state ──────────────────────────────────────────────────
  const [savedCohorts, setSavedCohorts] = useState<SavedCohort[]>([
    {
      id: 'cohort-1',
      name: 'Unreviewed Environmental',
      filters: {
        typeFilters: [...DISPLAY_TYPES],
        threadFilter: 'all',
        reviewFilter: 'unreviewed',
        classificationFilter: 'all',
        privilegedFilter: 'all',
        issueFilter: 'issue-3',
        participantSearch: '',
        dateFrom: '',
        dateTo: '',
        groupByFamily: false,
        hideDuplicates: false,
      },
      createdAt: '2023-10-20 09:00',
      docCount: 12,
    },
    {
      id: 'cohort-2',
      name: 'Hot + Critical Docs',
      filters: {
        typeFilters: [...DISPLAY_TYPES],
        threadFilter: 'all',
        reviewFilter: 'all',
        classificationFilter: 'hot',
        privilegedFilter: 'all',
        issueFilter: null,
        participantSearch: '',
        dateFrom: '',
        dateTo: '',
        groupByFamily: false,
        hideDuplicates: false,
      },
      createdAt: '2023-10-21 14:30',
      docCount: 5,
    },
  ])
  const [activeCohortId, setActiveCohortId] = useState<string | null>(null)

  const getCurrentFilterSnapshot = (): FilterSnapshot => ({
    typeFilters: Array.from(typeFilters),
    threadFilter,
    reviewFilter,
    classificationFilter,
    privilegedFilter,
    issueFilter,
    participantSearch,
    dateFrom,
    dateTo,
    groupByFamily,
    hideDuplicates,
  })

  const applyFilterSnapshot = (filters: FilterSnapshot) => {
    setTypeFilters(new Set(filters.typeFilters))
    setThreadFilter(filters.threadFilter)
    setReviewFilter(filters.reviewFilter)
    setClassificationFilter(filters.classificationFilter)
    setPrivilegedFilter(filters.privilegedFilter)
    setIssueFilter(filters.issueFilter)
    setParticipantSearch(filters.participantSearch)
    setDateFrom(filters.dateFrom)
    setDateTo(filters.dateTo)
    setGroupByFamily(filters.groupByFamily)
    setHideDuplicates(filters.hideDuplicates)
  }

  // Clear active cohort if any filter changes
  useEffect(() => {
    if (!activeCohortId) return
    const cohort = savedCohorts.find((c) => c.id === activeCohortId)
    if (!cohort) return

    const current = getCurrentFilterSnapshot()
    const isMatch =
      JSON.stringify(current.typeFilters.sort()) ===
        JSON.stringify(cohort.filters.typeFilters.sort()) &&
      current.threadFilter === cohort.filters.threadFilter &&
      current.reviewFilter === cohort.filters.reviewFilter &&
      current.classificationFilter === cohort.filters.classificationFilter &&
      current.privilegedFilter === cohort.filters.privilegedFilter &&
      current.issueFilter === cohort.filters.issueFilter &&
      current.participantSearch === cohort.filters.participantSearch &&
      current.dateFrom === cohort.filters.dateFrom &&
      current.dateTo === cohort.filters.dateTo &&
      current.groupByFamily === cohort.filters.groupByFamily &&
      current.hideDuplicates === cohort.filters.hideDuplicates

    if (!isMatch) {
      setActiveCohortId(null)
    }
  }, [
    typeFilters,
    threadFilter,
    reviewFilter,
    classificationFilter,
    privilegedFilter,
    issueFilter,
    participantSearch,
    dateFrom,
    dateTo,
    groupByFamily,
    hideDuplicates,
    activeCohortId,
    savedCohorts,
  ])

  const saveCohort = (name: string) => {
    const cohort: SavedCohort = {
      id: `cohort-${Date.now()}`,
      name,
      filters: getCurrentFilterSnapshot(),
      createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
      docCount: filteredDocs.length,
    }
    setSavedCohorts((prev) => [...prev, cohort])
    setActiveCohortId(cohort.id)
  }

  const loadCohort = (cohortId: string) => {
    const cohort = savedCohorts.find((c) => c.id === cohortId)
    if (!cohort) return
    applyFilterSnapshot(cohort.filters)
    setActiveCohortId(cohortId)
  }

  const deleteCohort = (cohortId: string) => {
    setSavedCohorts((prev) => prev.filter((c) => c.id !== cohortId))
    if (activeCohortId === cohortId) {
      setActiveCohortId(null)
    }
  }

  const clearCohort = () => {
    setActiveCohortId(null)
    clearAllFilters()
  }

  // ── Issue toggle ──────────────────────────────────────────────────
  const handleToggleIssue = (issueId: string) => {
    if (!selectedDoc) return
    setDocIssues((prev) => {
      const current = prev[selectedDoc.id] || []
      if (current.includes(issueId)) {
        return {
          ...prev,
          [selectedDoc.id]: current.filter((id) => id !== issueId),
        }
      } else {
        return { ...prev, [selectedDoc.id]: [...current, issueId] }
      }
    })
  }

  // ── Comments ──────────────────────────────────────────────────────
  useEffect(() => {
    if (drawerTab === 'comments') {
      commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [drawerTab, comments])

  const handlePostComment = () => {
    if (!newComment.trim() || !selectedDoc) return
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ')
    const comment: Comment = {
      id: `c-${Date.now()}`,
      author: 'You',
      initials: 'JD',
      text: newComment.trim(),
      timestamp: now,
      isCurrentUser: true,
    }
    setComments((prev) => ({
      ...prev,
      [selectedDoc.id]: [...(prev[selectedDoc.id] || []), comment],
    }))
    setNewComment('')
    commentInputRef.current?.focus()

    // If TBD is pending, confirm the classification now
    if (pendingTbd) {
      setPendingTbd(false)
      setDocStates((prev) => {
        const current = prev[selectedDoc.id] || DEFAULT_REVIEW_STATE
        return {
          ...prev,
          [selectedDoc.id]: {
            ...current,
            reviewed: true,
            reviewedBy: current.reviewedBy || 'You',
            reviewedAt: current.reviewedAt || now,
            classification: 'tbd',
            classifiedBy: 'You',
          },
        }
      })
    }
  }

  const getDocComments = (docId: string) => comments[docId] || []

  // ── Arrow key navigation ──────────────────────────────────────────
  const visibleFlatDocs = useMemo(
    () => paginatedRows.map((r) => r.doc),
    [paginatedRows],
  )
  const selectedIndex = selectedDoc
    ? visibleFlatDocs.findIndex((d) => d.id === selectedDoc.id)
    : -1

  const navigatePrev = useCallback(() => {
    if (selectedIndex > 0) setSelectedDoc(visibleFlatDocs[selectedIndex - 1])
  }, [selectedIndex, visibleFlatDocs])

  const navigateNext = useCallback(() => {
    if (selectedIndex < visibleFlatDocs.length - 1)
      setSelectedDoc(visibleFlatDocs[selectedIndex + 1])
  }, [selectedIndex, visibleFlatDocs])

  // ── Review & Classification handlers ──────────────────────────────
  const handleReviewAndNext = useCallback(() => {
    if (!selectedDoc) return
    const state = docStates[selectedDoc.id] || DEFAULT_REVIEW_STATE
    if (state.reviewed) return // Don't re-review
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ')
    setDocStates((prev) => ({
      ...prev,
      [selectedDoc.id]: {
        ...(prev[selectedDoc.id] || DEFAULT_REVIEW_STATE),
        reviewed: true,
        reviewedBy: 'You',
        reviewedAt: now,
      },
    }))
    navigateNext()
  }, [selectedDoc, docStates, navigateNext])

  useEffect(() => {
    if (!selectedDoc && !aiDrawerOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        navigatePrev()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        navigateNext()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setSelectedDoc(null)
        closeAiDrawer()
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault()
        handleReviewAndNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    selectedDoc,
    aiDrawerOpen,
    navigatePrev,
    navigateNext,
    handleReviewAndNext,
    closeAiDrawer,
  ])

  const handleUnreview = () => {
    if (!selectedDoc) return
    setDocStates((prev) => ({
      ...prev,
      [selectedDoc.id]: {
        ...(prev[selectedDoc.id] || DEFAULT_REVIEW_STATE),
        reviewed: false,
        reviewedBy: undefined,
        reviewedAt: undefined,
      },
    }))
  }

  // ── Pending TBD state ─────────────────────────────────────────────
  const [pendingTbd, setPendingTbd] = useState(false)

  // Clear pending TBD when switching docs
  useEffect(() => {
    setPendingTbd(false)
  }, [selectedDoc?.id])

  const handleClassify = (classification: Classification) => {
    if (!selectedDoc) return

    // If clicking TBD, go to pending mode instead of applying immediately
    if (classification === 'tbd') {
      const current = docStates[selectedDoc.id] || DEFAULT_REVIEW_STATE
      if (current.classification === 'tbd') {
        // Already TBD — toggle it off
        const now = new Date().toISOString().slice(0, 16).replace('T', ' ')
        setPendingTbd(false)
        setDocStates((prev) => ({
          ...prev,
          [selectedDoc.id]: {
            ...current,
            reviewed: true,
            reviewedBy: current.reviewedBy || 'You',
            reviewedAt: current.reviewedAt || now,
            classification: null,
            classifiedBy: undefined,
          },
        }))
      } else {
        // Enter pending TBD mode — switch to comments tab
        setPendingTbd(true)
        setDrawerTab('comments')
        setTimeout(() => commentInputRef.current?.focus(), 100)
      }
      return
    }

    // Non-TBD classifications apply immediately (and clear pending TBD)
    setPendingTbd(false)
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ')
    setDocStates((prev) => {
      const current = prev[selectedDoc.id] || DEFAULT_REVIEW_STATE
      const newClassification =
        current.classification === classification ? null : classification
      return {
        ...prev,
        [selectedDoc.id]: {
          ...current,
          reviewed: true,
          reviewedBy: current.reviewedBy || 'You',
          reviewedAt: current.reviewedAt || now,
          classification: newClassification,
          classifiedBy: newClassification ? 'You' : undefined,
        },
      }
    })
  }

  const handleTogglePrivileged = () => {
    if (!selectedDoc) return
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ')
    setDocStates((prev) => {
      const current = prev[selectedDoc.id] || DEFAULT_REVIEW_STATE
      const isPriv = !current.privileged
      return {
        ...prev,
        [selectedDoc.id]: {
          ...current,
          privileged: isPriv,
          privilegedBy: isPriv ? 'You (Admin)' : undefined,
          privilegedAt: isPriv ? now : undefined,
        },
      }
    })
  }

  // ── Hover preview handlers ────────────────────────────────────────
  const handleRowMouseEnter = (
    doc: ReviewDocument,
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    const row = e.currentTarget
    hoverTimerRef.current = setTimeout(() => {
      if (selectedDoc?.id === doc.id) return
      const rect = row.getBoundingClientRect()
      setHoverPosition({
        top: rect.top - 4,
        left: rect.left + rect.width * 0.35,
      })
      setHoveredDoc(doc)
    }, 300)
  }

  const handleRowMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    setHoveredDoc(null)
  }

  // ══════════════════════════════════════════════════════════════════
  // RETURN
  // ══════════════════════════════════════════════════════════════════
  return {
    // Filter state
    searchQuery,
    setSearchQuery,
    sidebarOpen,
    setSidebarOpen,
    typeFilters,
    threadFilter,
    setThreadFilter,
    reviewFilter,
    setReviewFilter,
    classificationFilter,
    setClassificationFilter,
    privilegedFilter,
    setPrivilegedFilter,
    issueFilter,
    setIssueFilter,
    commentFilter,
    setCommentFilter,
    docsWithCommentsCount: Object.keys(comments).filter(
      (id) => comments[id].length > 0,
    ).length,
    groupByFamily,
    setGroupByFamily,
    hideDuplicates,
    setHideDuplicates,
    participantSearch,
    setParticipantSearch,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    collapsedSections,

    // Sort
    sortColumn,
    sortDirection,
    handleSort,

    // Pagination
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalFiltered,
    showingFrom,
    showingTo,
    totalPages,
    safeCurrentPage,
    paginatedRows,

    // Threads
    expandedThreads,
    toggleThread,

    // Columns
    visibleColumns,
    gridTemplate,
    isColumnVisible,
    toggleColumn,
    columnDropdownOpen,
    setColumnDropdownOpen,
    columnDropdownRef,
    pageSizeDropdownOpen,
    setPageSizeDropdownOpen,
    pageSizeDropdownRef,

    // Filter helpers
    activeFilterCount,
    hasActiveFilters,
    clearAllFilters,
    toggleSection,
    toggleTypeFilter,
    allSenders: ALL_SENDERS,

    // Doc review state
    docStates,
    getDocState,

    // Issues
    caseIssues,
    docIssues,
    handleToggleIssue,

    // Selected doc + drawer
    selectedDoc,
    setSelectedDoc,
    drawerTab,
    setDrawerTab,
    selectedIndex,
    visibleFlatDocs,
    navigatePrev,
    navigateNext,

    // Comments
    newComment,
    setNewComment,
    handlePostComment,
    getDocComments,
    commentsEndRef,
    commentInputRef,

    // Review actions
    handleReviewAndNext,
    handleUnreview,
    handleClassify,
    handleTogglePrivileged,
    pendingTbd,

    // Hover
    hoveredDoc,
    setHoveredDoc,
    hoverPosition,
    handleRowMouseEnter,
    handleRowMouseLeave,

    // Cohorts
    savedCohorts,
    activeCohortId,
    saveCohort,
    loadCohort,
    deleteCohort,
    clearCohort,

    // AI
    aiDrawerOpen,
    aiMessages,
    aiHistory,
    aiInput,
    setAiInput,
    aiLoading,
    aiFilteredDocIds,
    aiTab,
    setAiTab,
    aiMessagesEndRef,
    openAiDrawer,
    closeAiDrawer,
    handleAiSend,
    applyAiCohort,
    clearAiFilter,
    startNewAiChat,
    loadAiConversation,

    // Static helpers
    getThreadSize,
    getDocFamily,
  }
}
